import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { API_CONFIG } from '../config/api';
// ✅ Import Heroicons for professional icons
import {
  HomeIcon,
  CpuChipIcon,
  BoltIcon,
  ComputerDesktopIcon,
  CircleStackIcon,
  CogIcon,
  BellIcon,
  // Database Agent icons
  MagnifyingGlassIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  ArrowTrendingUpIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  // Additional icons for Train Agent section
  PencilIcon,
  TrashIcon,
  PauseIcon,
  CheckCircleIcon,
  // Profile dropdown icons
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  BookOpenIcon,
  ShareIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import SharedPagination from './shared/Pagination'; // NEW: Import shared pagination component
// ✅ NEW IMPORTS for Agent Management
import CreateAgentForm from '../components/CreateAgentForm';
import AgentList from '../components/AgentList';
import RecentActivityFeed from '../components/RecentActivityFeed'; // NEW: Import RecentActivityFeed
import HomeStatsRow from '../components/HomeStatsRow'; // Home page stats + chart
// ✅ NEW IMPORTS for Cognix Agent
import CognixAgentList from '../components/CognixAgentList';
import { cognixAgentService } from '../services/cognixAgentService';
// ✅ NEW IMPORTS for Cognix Tools
import CognixToolList from '../components/CognixToolList';
// ✅ NEW IMPORTS for Knowledge Base
// ✅ NEW IMPORTS for Workflow
import WorkflowProjects from '../components/WorkflowProjects';
import WorkflowRunModal from '../components/WorkflowRunModal';
import { notificationService } from '../services/notificationService';
import EnhancedChartVisualization from './EnhancedChartVisualization';

// Code-split: heavy screens load on demand (see Suspense boundary below).
const DataSourcesPage = lazy(() => import('./DataSourcesPage'));
const ExcelUploadPage = lazy(() => import('./ExcelUploadPage'));
const ExcelAgent = lazy(() => import('./ExcelAgent'));
const AutomateTask = lazy(() => import('./AutomateTask'));
const Settings = lazy(() => import('./Settings.jsx'));
const DataPreview = lazy(() => import('./DataPreview'));
const AnalyticsDashboard = lazy(() => import('./analytics/AnalyticsDashboard'));
const CognixAgentBuilder = lazy(() => import('../components/CognixAgentBuilder'));
const CognixAgentChat = lazy(() => import('../components/CognixAgentChat'));
const KnowledgeBase = lazy(() => import('../components/KnowledgeBase'));
const WorkflowBuilder = lazy(() => import('../components/WorkflowBuilder'));
const AIAssistant = lazy(() => import('./AIAssistant'));

export default function Dashboard() {
  console.log("🔥 DASHBOARD COMPONENT LOADED - VERSION 2.0");
  
  const [activeTab, setActiveTab] = useState('home');
  const [askDBActiveTab, setAskDBActiveTab] = useState('askdb'); // Default to askdb when agent is selected
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // ✅ NEW STATE for Agent Management
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showCreateAgentForm, setShowCreateAgentForm] = useState(false);
  const [agentRefreshTrigger, setAgentRefreshTrigger] = useState(0);
  const [showAgentInteractionMode, setShowAgentInteractionMode] = useState(false); // New state to control view mode
  
  // ✅ NEW STATE for Feedback System
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    messageIndex: null,
    userQuestion: '',
    feedbackText: ''
  });
  const [messageFeedback, setMessageFeedback] = useState({}); // Track feedback state for each message
  const [collapsedThinking, setCollapsedThinking] = useState({}); // Track collapsed state for thinking sections

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // ✅ NEW STATE for Workflow module
  const [workflowView, setWorkflowView] = useState('projects'); // 'projects' | 'builder'
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowProjectId, setWorkflowProjectId] = useState(null);
  const [runningWorkflow, setRunningWorkflow] = useState(null);
  const [, setWorkflowRefreshTrigger] = useState(0);
  
  // ✅ NEW STATE for Chat Message Persistence
  const [currentConversationId, setCurrentConversationId] = useState(null);
  
  // ✅ NEW STATE for Profile Dropdown
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  // Quick-start trigger for Data Sources tab (controls which modal opens)
  const [dataSourcesQuickStart, setDataSourcesQuickStart] = useState(null);

  // Sidebar expansion for Agent submenu
  const [isAgentMenuOpen, setIsAgentMenuOpen] = useState(false);
  const [agentFlyoutPos, setAgentFlyoutPos] = useState({ top: 0, left: 0 });
  const agentButtonRef = useRef(null);

  // Notification panel portal position
  const [notifPanelPos, setNotifPanelPos] = useState({ bottom: 0, left: 0 });
  const notifButtonRef = useRef(null);
  
  // ✅ NEW STATE for Data Table Pagination
  const [tablePagination, setTablePagination] = useState({});

  // ✅ NEW STATE for Cognix Agent
  const [cognixAgentView, setCognixAgentView] = useState('list'); // list, builder, chat
  const [cognixPageTab, setCognixPageTab] = useState('agent'); // 'agent' | 'tools'
  const [selectedCognixAgent, setSelectedCognixAgent] = useState(null);
  const [cognixAgentRefreshTrigger, setCognixAgentRefreshTrigger] = useState(0);
  const [cognixAgentLoading, setCognixAgentLoading] = useState(false);
  const [cognixAgentError, setCognixAgentError] = useState('');

  // ✅ NEW STATE for Cognix Tools
  const [cognixToolRefreshTrigger] = useState(0);

  // Home banner stats
  const [homeStats, setHomeStats] = useState(null);

  const { user, logout } = useAuth();

  const messagesContainerRef = useRef(null);

  // ✅ NEW: Auto-scroll to bottom function
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Vertical sidebar tabs with professional icons
  const sidebarTabs = [
    { id: 'home', name: 'Home', icon: HomeIcon },
    { id: 'ai-assistant', name: 'AI Assistant', icon: SparklesIcon },
    {
      id: 'agent',
      name: 'Agents',
      icon: CpuChipIcon,
      children: [
        { id: 'cognix-agent', name: 'Cognix AI Agent' },
        { id: 'database-agent', name: 'Database Agent' },
        { id: 'excel-agent', name: 'Excel Agent' },
      ]
    },
    { id: 'workflow', name: 'Workflow', icon: ShareIcon },
    { id: 'automate-task', name: 'Workflow Monitor', icon: ComputerDesktopIcon },
    { id: 'data-preview', name: 'Data Preview', icon: TableCellsIcon },
    { id: 'analytics', name: 'Analytics', icon: ChartBarIcon },
    // { id: 'business-glossary', name: 'Business Glossary', icon: DocumentTextIcon }, // hidden: replaced by Knowledge Base
    { id: 'knowledge-base', name: 'Knowledge Base', icon: BookOpenIcon },
    { id: 'data-sources', name: 'Data Sources', icon: CircleStackIcon },
    // ✅ Removed 'settings' - now accessible only through Profile dropdown for better UX
  ];

  // ✅ NEW: Animated dots component for loading states
  // eslint-disable-next-line no-unused-vars
  const AnimatedDots = () => {
    const [dots, setDots] = useState('');
    
    useEffect(() => {
      const interval = setInterval(() => {
        setDots(prev => {
          if (prev === '...') return '';
          return prev + '.';
        });
      }, 500);
      
      return () => clearInterval(interval);
    }, []);
    
    return <span>{dots}</span>;
  };

  const loadingSteps = useMemo(() => [
    "Analyzing your question",
    "Understanding database schema", 
    "Generating SQL with AI",
    "Executing query on PostgreSQL",
    "Processing results",
    "Generating insights"
  ], []);

  // CSV download function
  const downloadCSV = (data, filename = 'query_results.csv') => {
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }

    const keys = Object.keys(data[0]);
    
    // Create CSV content
    const csvContent = [
      // Header row
      keys.join(','),
      // Data rows
      ...data.map(row => 
        keys.map(key => {
          const value = row[key];
          // Handle values with commas, quotes, or newlines
          if (value === null || value === undefined) {
            return '';
          }
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    console.log('🚀 CognixAI Dashboard mounted');
    console.log('👤 Current user:', user);
  }, [user]);

  // Notification polling
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationService.list();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!showNotifications) return;
    const handler = (e) => { if (!e.target.closest('[data-notification-panel]')) setShowNotifications(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifications]);

  // Fetch home stats for hero banner strip
  useEffect(() => {
    if (activeTab !== 'home') return;
    const token = localStorage.getItem('token');
    axios.get(`${API_CONFIG.API_BASE}/dashboard/home-stats`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (r.data.success) setHomeStats(r.data.stats);
    }).catch(() => {});
  }, [activeTab]);

  // ✅ Handle clicking outside profile dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  useEffect(() => {
    let interval;
    if (isLoading) {
      interval = setInterval(() => {
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.isLoading ? { ...msg, content: loadingSteps[Math.floor(Math.random() * loadingSteps.length)] } : msg
          )
        );
      }, 1500);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading, loadingSteps]);

    // Reset quick-start after leaving Data Sources tab
    useEffect(() => {
      if (activeTab !== 'data-sources' && dataSourcesQuickStart) {
        setDataSourcesQuickStart(null);
      }
    }, [activeTab, dataSourcesQuickStart]);

  // ✅ OPTIMIZED: Memoized chat message loading to prevent unnecessary calls
  const loadChatMessages = useCallback(async (conversationId = null) => {
    if (!selectedAgent) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const url = conversationId 
        ? `${API_CONFIG.API_BASE}/chat/conversations?conversation_id=${conversationId}&limit=50`
        : `${API_CONFIG.API_BASE}/chat/conversations?agent_id=${selectedAgent.agent_id}&limit=50`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load conversations');
      
      const data = await response.json();
      if (data.success && data.conversations) {
        const uiMessages = data.conversations.flatMap(conv => {
          // Extract reasoning/thinking process from ai_response if it contains the pattern
          let aiContent = conv.ai_response || 'No response available';
          let thinkingProcess = '';
          
          // Check if ai_response contains reasoning pattern
          if (aiContent.includes('**Analyze Question:**')) {
            const parts = aiContent.split('**Analyze Question:**');
            aiContent = parts[0].trim() || 'Query executed successfully!';
            thinkingProcess = '**Analyze Question:**' + parts.slice(1).join('**Analyze Question:**');
          }
          
          return [
            { type: 'user', content: conv.user_question },
            { 
              type: 'ai', 
              content: aiContent,
              thinkingProcess: thinkingProcess, // ✅ Always map thinking process
              sqlQuery: conv.sql_query,
              data: conv.query_results || [],
              insights: conv.ai_insights,
              conversationId: conv.conversation_id
            }
          ];
        });
        setMessages(uiMessages);
      }
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  }, [selectedAgent]);

  // ✅ OPTIMIZED: Only auto-load chat messages when actually needed (not while a query is in flight)
  useEffect(() => {
    if (activeTab === 'database-agent' && askDBActiveTab === 'askdb' && selectedAgent && !isLoading) {
      loadChatMessages();
    }
  }, [activeTab, askDBActiveTab, selectedAgent?.agent_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ NEW: Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ✅ NEW: Auto-scroll when switching to askdb tab or when agent is selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'database-agent' && askDBActiveTab === 'askdb' && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100);
    }
  }, [activeTab, askDBActiveTab, selectedAgent]);

  // ✅ UPDATED: Agent Management Functions with auto-redirect and data validation
  const handleAgentCreated = (newAgent) => {
    console.log('New agent created:', newAgent);
    setShowCreateAgentForm(false);
    setAgentRefreshTrigger(prev => prev + 1);
    
    // ✅ FIXED: Ensure selected_tables is always an array
    const validatedAgent = {
      ...newAgent,
      selected_tables: Array.isArray(newAgent.selected_tables) 
        ? newAgent.selected_tables 
        : [],
      agent_id: newAgent.agent_id || newAgent.id // Handle different response formats
    };
    
    setSelectedAgent(validatedAgent);
    setAskDBActiveTab('askdb'); // ✅ Auto-redirect to AskDB
    
    // Show success message
    alert(`Agent "${newAgent.agent_name}" created successfully with ${newAgent.tables_processed || 0} tables! Redirecting to AskDB...`);
  };

  const handleAgentSelected = (agent) => {
    console.log('Agent selected:', agent);
    
    // ✅ FIXED: Ensure selected_tables is always an array
    const validatedAgent = {
      ...agent,
      selected_tables: Array.isArray(agent.selected_tables) 
        ? agent.selected_tables 
        : []
    };
    
    setSelectedAgent(validatedAgent);
    setShowAgentInteractionMode(true); // Show AskDB and Train Agent tabs
    setAskDBActiveTab('askdb'); // ✅ Auto-redirect to AskDB when agent is selected
  };

  // ✅ NEW: Function to handle agent click from recent activity
  const handleAgentClickFromActivity = async (agentName) => {
    try {
      console.log('🔍 Searching for agent:', agentName);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.API_BASE}/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const agent = data.agents.find(a => a.agent_name === agentName);
        
        if (agent) {
          // Navigate to Agent module (Database tab) and select this agent
          setIsAgentMenuOpen(true);
          setActiveTab('database-agent');
          handleAgentSelected(agent);
          console.log('✅ Agent found and selected:', agent.agent_name);
        } else {
          console.warn('⚠️ Agent not found:', agentName);
          // Still navigate to agent module but show agent list
          setIsAgentMenuOpen(true);
          setActiveTab('database-agent');
        }
      }
    } catch (error) {
      console.error('❌ Error finding agent:', error);
      // Fallback: navigate to agent module
      setIsAgentMenuOpen(true);
      setActiveTab('database-agent');
    }
  };

  // ✅ NEW: Function to go back to agent selection
  const handleBackToAgents = () => {
    setShowAgentInteractionMode(false);
    setSelectedAgent(null);
    setAskDBActiveTab('askdb'); // Reset to default
  };

  // ✅ NEW: Feedback System Functions
  const handleThumbsUp = async (messageIndex) => {
    console.log('👍 Thumbs up for message:', messageIndex);
    
    // Update local state to show thumbs up as selected
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: { type: 'positive', submitted: true }
    }));

    // Send positive feedback to backend
    try {
      const userQuestion = findUserQuestionForMessage(messageIndex);
      
      const response = await axios.post(`${API_CONFIG.API_BASE}/feedback`, {
        user_question: userQuestion,
        feedback_text: 'User found this response helpful',
        feedback_type: 'positive',
        agent_name: selectedAgent ? selectedAgent.agent_name : null,
        message_index: messageIndex
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Positive feedback submitted:', response.data);
    } catch (error) {
      console.error('❌ Error submitting positive feedback:', error);
      // Revert UI state on error
      setMessageFeedback(prev => ({
        ...prev,
        [messageIndex]: { type: null, submitted: false }
      }));
    }
  };

  const handleThumbsDown = (messageIndex) => {
    console.log('👎 Thumbs down for message:', messageIndex);
    
    // Find the user question that led to this response
    const userQuestion = findUserQuestionForMessage(messageIndex);
    
    // Open feedback modal
    setFeedbackModal({
      isOpen: true,
      messageIndex,
      userQuestion,
      feedbackText: ''
    });
  };

  const findUserQuestionForMessage = (messageIndex) => {
    // Look backwards from the current message to find the last user message
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i] && messages[i].type === 'user') {
        return messages[i].content;
      }
    }
    return 'Unknown question';
  };

  const handleFeedbackSubmit = async () => {
    console.log('📝 Submitting feedback:', feedbackModal);
    
    try {
      // Update local state to show thumbs down as selected
      setMessageFeedback(prev => ({
        ...prev,
        [feedbackModal.messageIndex]: { type: 'negative', submitted: true }
      }));

      // Send negative feedback to backend
      const response = await axios.post(`${API_CONFIG.API_BASE}/feedback`, {
        user_question: feedbackModal.userQuestion,
        feedback_text: feedbackModal.feedbackText,
        feedback_type: 'negative',
        agent_name: selectedAgent ? selectedAgent.agent_name : null,
        message_index: feedbackModal.messageIndex
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Negative feedback submitted:', response.data);
      
      // Close modal and reset
      setFeedbackModal({
        isOpen: false,
        messageIndex: null,
        userQuestion: '',
        feedbackText: ''
      });

      // Show success message
      alert('Thank you for your feedback! We\'ll use it to improve our responses.');
      
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      
      // Revert UI state on error
      setMessageFeedback(prev => ({
        ...prev,
        [feedbackModal.messageIndex]: { type: null, submitted: false }
      }));
      
      alert('Error submitting feedback. Please try again.');
    }
  };

  const closeFeedbackModal = () => {
    setFeedbackModal({
      isOpen: false,
      messageIndex: null,
      userQuestion: '',
      feedbackText: ''
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // eslint-disable-next-line no-unused-vars
  const getNumericColumns = (data) => {
    if (!data || data.length === 0) return [];
    
    const sample = data[0];
    return Object.keys(sample).filter(key => {
      const value = sample[key];
      return !isNaN(value) && !isNaN(parseFloat(value)) && isFinite(value);
    });
  };

  const getTopValues = (values, limit = 3) => {
    const counts = {};
    values.forEach(val => {
      const key = String(val).toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    });
    
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([value, count]) => ({ value, count }));
  };

  const analyzeTextPatterns = (values) => {
    const patterns = [];
    
    const emailPattern = /\S+@\S+\.\S+/;
    const emails = values.filter(val => emailPattern.test(String(val)));
    if (emails.length > 0) {
      patterns.push(`${emails.length} email addresses`);
    }
    
    const datePattern = /\d{4}-\d{2}-\d{2}/;
    const dates = values.filter(val => datePattern.test(String(val)));
    if (dates.length > 0) {
      patterns.push(`${dates.length} date formats`);
    }
    
    const urlPattern = /https?:\/\/\S+/;
    const urls = values.filter(val => urlPattern.test(String(val)));
    if (urls.length > 0) {
      patterns.push(`${urls.length} URLs`);
    }
    
    const avgLength = values.reduce((sum, val) => sum + String(val).length, 0) / values.length;
    if (avgLength > 50) {
      patterns.push('Long text content');
    } else if (avgLength < 10) {
      patterns.push('Short identifiers');
    }
    
    return patterns;
  };

  const findColumnRelationships = (data, columns) => {
    const relationships = [];
    
    const idColumns = columns.filter(col => 
      col.toLowerCase().includes('id') || 
      col.toLowerCase().includes('key')
    );
    
    if (idColumns.length > 1) {
      relationships.push(`${idColumns.length} identifier columns detected`);
    }
    
    const nameColumns = columns.filter(col => 
      col.toLowerCase().includes('name') || 
      col.toLowerCase().includes('title')
    );
    
    if (nameColumns.length > 0) {
      relationships.push(`${nameColumns.length} name/title columns`);
    }
    
    if (columns.length === 2) {
      const [col1, col2] = columns;
      const unique1 = new Set(data.map(row => row[col1])).size;
      const unique2 = new Set(data.map(row => row[col2])).size;
      
      if (unique1 < data.length * 0.5 && unique2 > unique1) {
        relationships.push(`${col1} appears to categorize ${col2}`);
      }
    }
    
    return relationships;
  };

  const generateContentInsights = (data, originalQuery) => {
    const insights = [];
    const queryLower = originalQuery.toLowerCase();
    
    if (queryLower.includes('user') || queryLower.includes('customer')) {
      insights.push(`**User Analysis**: Query focused on user/customer data`);
    }
    
    if (queryLower.includes('date') || queryLower.includes('time') || queryLower.includes('month') || queryLower.includes('year')) {
      insights.push(`**Temporal Analysis**: Time-based data query`);
    }
    
    if (queryLower.includes('revenue') || queryLower.includes('sales') || queryLower.includes('profit')) {
      insights.push(`**Financial Analysis**: Revenue/sales performance data`);
    }
    
    if (queryLower.includes('activity') || queryLower.includes('action') || queryLower.includes('event')) {
      insights.push(`**Activity Analysis**: Behavioral/event tracking data`);
    }
    
    return insights;
  };

  const generateInsights = (data, sqlQuery, originalQuery) => {
    if (!data || data.length === 0) {
      return "No data was returned from your query.";
    }

    const rowCount = data.length;
    const columns = Object.keys(data[0]);
    let insights = [];

    insights.push(`**Data Overview**: Found ${rowCount} records with ${columns.length} columns (${columns.join(', ')})`);

    columns.forEach(col => {
      const values = data.map(row => row[col]).filter(val => val !== null && val !== undefined && val !== '');
      const uniqueValues = [...new Set(values)];
      const nullCount = data.length - values.length;
      
      const numericValues = values.filter(val => !isNaN(val) && !isNaN(parseFloat(val)) && isFinite(val));
      const isNumeric = numericValues.length > values.length * 0.8;
      
      if (isNumeric && numericValues.length > 0) {
        const nums = numericValues.map(val => parseFloat(val));
        const sum = nums.reduce((a, b) => a + b, 0);
        const avg = sum / nums.length;
        const max = Math.max(...nums);
        const min = Math.min(...nums);
        
        insights.push(`**${col}** (Numeric): Avg: ${avg.toFixed(2)}, Range: ${min} - ${max}, Total: ${sum.toFixed(2)}`);
      } else {
        const topValues = getTopValues(values, 3);
        const diversity = ((uniqueValues.length / values.length) * 100).toFixed(1);
        
        let textInsight = `**${col}** (Text): ${uniqueValues.length} unique values (${diversity}% diversity)`;
        
        if (topValues.length > 0) {
          const topValuesStr = topValues.map(item => `"${item.value}" (${item.count})`).join(', ');
          textInsight += `. Most common: ${topValuesStr}`;
        }
        
        if (values.length > 0) {
          const patterns = analyzeTextPatterns(values);
          if (patterns.length > 0) {
            textInsight += `. Patterns: ${patterns.join(', ')}`;
          }
        }
        
        insights.push(textInsight);
      }
      
      if (nullCount > 0) {
        insights.push(`**${col}**: ${nullCount} missing values (${((nullCount/data.length)*100).toFixed(1)}%)`);
      }
    });

    if (columns.length > 1) {
      const relationships = findColumnRelationships(data, columns);
      if (relationships.length > 0) {
        insights.push(`**Relationships**: ${relationships.join(', ')}`);
      }
    }

    const queryLower = sqlQuery.toLowerCase();
    const queryInsights = [];
    
    if (queryLower.includes('join')) {
      queryInsights.push('Used table joins for comprehensive data');
    }
    if (queryLower.includes('group by')) {
      queryInsights.push('Data grouped for summary analysis');
    }
    if (queryLower.includes('order by')) {
      queryInsights.push('Results ordered systematically');
    }
    if (queryLower.includes('where')) {
      queryInsights.push('Applied filtering conditions');
    }
    if (queryLower.includes('count')) {
      queryInsights.push('Performed count aggregation');
    }
    if (queryLower.includes('distinct')) {
      queryInsights.push('Removed duplicate values');
    }
    
    if (queryInsights.length > 0) {
      insights.push(`**Query Analysis**: ${queryInsights.join(', ')}`);
    }

    if (rowCount === 1) {
      insights.push(`**Single Record**: This query returned a specific individual record`);
    } else if (rowCount < 10) {
      insights.push(`**Small Dataset**: Focused result set with ${rowCount} records - ideal for detailed review`);
    } else if (rowCount < 100) {
      insights.push(`**Medium Dataset**: ${rowCount} records provide good sample size for analysis`);
    } else {
      insights.push(`**Large Dataset**: Substantial data with ${rowCount} records - consider pagination for performance`);
    }

    const contentInsights = generateContentInsights(data, originalQuery);
    if (contentInsights.length > 0) {
      insights.push(...contentInsights);
    }

    return insights.length > 0 ? insights.join('\n\n') : "Successfully executed your query!";
  };

  // ✅ NEW: Save complete conversation to database (one row per question-answer)
  const saveConversation = async (conversationData) => {
    try {
      console.log('� [DEBUG] saveConversation called with data:', conversationData);
      console.log('🚀 [DEBUG] Data keys:', Object.keys(conversationData));
      console.log('🚀 [DEBUG] Agent ID:', conversationData.agent_id);
      console.log('🚀 [DEBUG] Question length:', conversationData.user_question?.length);
      
      const response = await axios.post(
        `${API_CONFIG.API_BASE}/chat/conversations`,
        conversationData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('🚀 [DEBUG] Backend response:', response.data);
      console.log('🚀 [DEBUG] Response status:', response.status);
      
      if (response.data.success) {
        console.log('✅ [DEBUG] Conversation saved successfully:', response.data.conversation_id);
        return response.data.conversation_id;
      } else {
        console.error('❌ [DEBUG] Failed to save conversation:', response.data.error);
        return null;
      }
    } catch (error) {
      console.error('❌ [DEBUG] Error saving conversation:');
      console.error('❌ [DEBUG] Error message:', error.message);
      console.error('❌ [DEBUG] Error response:', error.response?.data);
      console.error('❌ [DEBUG] Error status:', error.response?.status);
      return null;
    }
  };

  // ✅ NEW: Clear all conversations for the current agent
  const clearAllConversations = async () => {
    if (!selectedAgent || !selectedAgent.agent_id) {
      console.error('❌ No agent selected for clearing conversations');
      return;
    }

    const confirmClear = window.confirm(
      `Are you sure you want to clear all chat conversations for agent "${selectedAgent.agent_name}"? This action cannot be undone.`
    );

    if (!confirmClear) return;

    try {
      console.log('🗑️ Clearing all conversations for agent:', selectedAgent.agent_id);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.API_BASE}/agents/${selectedAgent.agent_id}/conversations`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Conversations cleared successfully');
        
        // Clear messages from UI
        setMessages([]);
        
        // Reset conversation ID
        setCurrentConversationId(null);
        
        // Show success message
        alert(`Successfully cleared ${result.deleted_count} conversation(s) for "${selectedAgent.agent_name}"`);
        
      } else {
        console.error('❌ Failed to clear conversations:', result.error);
        alert('Failed to clear conversations. Please try again.');
      }

    } catch (error) {
      console.error('❌ Error clearing conversations:', error);
      alert('Error clearing conversations. Please check your connection and try again.');
    }
  };

  // ✅ UPDATED: Handle query submit with agent support
  // ✅ Start new conversation if needed
  const ensureConversationId = () => {
    if (!currentConversationId) {
      const newConversationId = crypto.randomUUID();
      setCurrentConversationId(newConversationId);
      return newConversationId;
    }
    return currentConversationId;
  };

  const handleQuerySubmit = async (e) => {
    e.preventDefault();
    
    // iOS Safari specific debug logging
    console.log('🍎 iOS Debug - handleQuerySubmit called');
    console.log('🍎 iOS Debug - query:', query);
    console.log('🍎 iOS Debug - isLoading:', isLoading);
    console.log('🍎 iOS Debug - selectedAgent:', selectedAgent);
    console.log('🍎 iOS Debug - User Agent:', navigator.userAgent);
    
    if (query.trim() && !isLoading) {
      console.log('🔍 Query submitted:', query);
      
      // ✅ Ensure we have a conversation ID
      ensureConversationId();
      
      const userMessage = { 
        type: 'user', 
        content: query,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      
      const loadingMessage = {
        type: 'ai',
        content: loadingSteps[0],
        timestamp: new Date().toLocaleTimeString(),
        isLoading: true
      };
      setMessages(prev => [...prev, loadingMessage]);
      
      const originalQuery = query;
      setQuery('');
      
      try {
        console.log('📡 Making API call to:', selectedAgent ? `/api/agents/${selectedAgent.agent_id}/query` : '/api/query/execute');
        
        const startTime = Date.now();
        
        // ✅ UPDATED: Use agent-specific endpoint if agent is selected
        const endpoint = selectedAgent 
          ? `${API_CONFIG.API_BASE}/agents/${selectedAgent.agent_id}/query`
          : `${API_CONFIG.API_BASE}/query/execute`;
        
        // iOS Safari specific: Add timeout and additional headers
        const requestConfig = {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'User-Agent': navigator.userAgent,
            'X-iOS-Safari': /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'true' : 'false'
          },
          timeout: 30000 // 30 second timeout for iOS
        };
        
        console.log('🍎 iOS Debug - Request config:', requestConfig);
        
        const response = await axios.post(endpoint, {
          question: originalQuery,
          database_type: 'postgresql'
        }, requestConfig);
        
        const endTime = Date.now();
        const executionTime = ((endTime - startTime) / 1000).toFixed(2);
        
        console.log('✅ API Response received:', response.data);
        console.log('🔧 Generated SQL:', response.data.sql_generated);
        console.log('📊 Query Results:', response.data.data);
        console.log('🤖 Raw LLM Response:', response.data.raw_response);
        console.log(`⏱️ Total execution time: ${executionTime}s`);
        
        setMessages(prev => prev.filter(msg => !msg.isLoading));
        
        if (response.data.success) {
          let aiExplanation = '';
          let thinkingProcess = '';
          
          // Always try to extract thinking process from raw response first
          if (response.data.raw_response && typeof response.data.raw_response === 'object') {
            thinkingProcess = response.data.raw_response.Thinking_Process || '';
            console.log('🧠 [DEBUG] Extracted thinking process:', thinkingProcess ? 'YES' : 'NO', thinkingProcess?.substring(0, 100));
          }
          
          if (response.data.explanation) {
            aiExplanation = response.data.explanation;
          } else if (response.data.raw_response && typeof response.data.raw_response === 'object') {
            // Get explanation from different possible fields, but NOT from Thinking_Process
            aiExplanation = response.data.raw_response.Explanation || 
                           response.data.raw_response.explanation || 
                           response.data.raw_response.response || 
                           'Query executed successfully!';
          } else if (response.data.raw_response && typeof response.data.raw_response === 'string') {
            aiExplanation = response.data.raw_response;
          } else {
            aiExplanation = 'Query executed successfully! Data retrieved from your database.';
          }

          const generatedInsights = generateInsights(
            response.data.data, 
            response.data.sql_generated, 
            originalQuery
          );

          // ✅ Save complete conversation to database in one row
          console.log('🚀 [DEBUG] Preparing to save conversation...');
          
          // ✅ Store complete response including thinking process
          const completeResponse = thinkingProcess ? 
            `${aiExplanation}\n\n**Analyze Question:**\n${thinkingProcess}` : 
            aiExplanation;
          
          const conversationData = {
            agent_id: selectedAgent?.agent_id || null,
            user_question: originalQuery,
            ai_response: completeResponse, // ✅ Store complete response with thinking process
            sql_query: response.data.sql_generated,
            ai_insights: generatedInsights,
            query_results: response.data.data, // Store the actual data
            row_count: response.data.data ? response.data.data.length : 0,
            execution_time: parseFloat(executionTime),
            is_success: true,
            agent_name: selectedAgent?.agent_name || null,
            datasource_name: response.data.datasource_name || null,
            datasource_type: response.data.datasource_type || null
          };
          
          console.log('🚀 [DEBUG] About to call saveConversation...');
          const savedConversationId = await saveConversation(conversationData);
          console.log('🚀 [DEBUG] saveConversation returned:', savedConversationId);

          const aiResponse = {
            type: 'ai',
            content: aiExplanation,
            thinkingProcess: thinkingProcess, // ✅ NEW: Add thinking process separately
            timestamp: new Date().toLocaleTimeString(),
            sqlQuery: response.data.sql_generated,
            data: response.data.data,
            rawResponse: response.data.raw_response,
            rowCount: response.data.data ? response.data.data.length : 0,
            executionTime: executionTime,
            insights: generatedInsights,
            isSuccess: true,
            originalQuery: originalQuery,
            // ✅ NEW: Add agent info if using agent
            agentInfo: selectedAgent ? {
              name: selectedAgent.agent_name,
              datasource: response.data.datasource_name,
              datasource_type: response.data.datasource_type
            } : null
          };
          
          // Add the AI response using current state (ONLY FIX - prevent user message disappearing)
          setMessages(prev => [...prev, aiResponse]);
          
          // ✅ NEW: Thinking process is expanded by default (undefined = expanded)
          // No need to explicitly set collapsedThinking since undefined = expanded by default
        } else {
          const errorResponse = {
            type: 'ai',
            content: `❌ Error: ${response.data.error}`,
            timestamp: new Date().toLocaleTimeString(),
            isError: true
          };
          setMessages(prev => [...prev, errorResponse]);
          
          // ✅ Save error conversation to database
          const errorConversationData = {
            agent_id: selectedAgent?.agent_id || null,
            user_question: originalQuery,
            ai_response: `Error: ${response.data.error}`,
            is_success: false,
            error_message: response.data.error,
            agent_name: selectedAgent?.agent_name || null
          };
          
          await saveConversation(errorConversationData);
        }
        
      } catch (error) {
        console.error('❌ API Error:', error);
        console.error('📋 Error Details:', error.response?.data);
        
        // iOS Safari specific error logging
        console.log('🍎 iOS Debug - Error occurred:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          isNetworkError: !error.response,
          userAgent: navigator.userAgent,
          isIOSSafari: /iPad|iPhone|iPod/.test(navigator.userAgent)
        });
        
        setMessages(prev => prev.filter(msg => !msg.isLoading));
        
        // iOS Safari specific error messages
        let errorContent = '';
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorContent = '❌ Request timeout - Please check your internet connection and try again';
        } else if (!error.response && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
          errorContent = '❌ Connection error - iOS Safari detected. Please ensure you have a stable internet connection and try again';
        } else {
          errorContent = `❌ Connection error: ${error.response?.data?.detail || error.message}`;
        }
        
        const errorMessage = {
          type: 'ai',
          content: errorContent,
          timestamp: new Date().toLocaleTimeString(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
        
        // ✅ Save connection error to database
        const connectionErrorData = {
          agent_id: selectedAgent?.agent_id || null,
          user_question: originalQuery,
          ai_response: errorContent,
          is_success: false,
          error_message: error.response?.data?.detail || error.message,
          agent_name: selectedAgent?.agent_name || null
        };
        
        try {
          await saveConversation(connectionErrorData);
        } catch (saveError) {
          console.error('❌ Error saving conversation error:', saveError);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };



  const renderDataTable = (data, messageIdx) => {
    if (!data || data.length === 0) return null;
    
    const keys = Object.keys(data[0]);
    const itemsPerPage = 10;
    
    // Get current page for this specific table (default to 1)
    const currentPage = tablePagination[messageIdx] || 1;
    
    // Calculate pagination
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayData = data.slice(startIndex, endIndex);
    
    // Pagination functions
    const goToPage = (page) => {
      setTablePagination(prev => ({
        ...prev,
        [messageIdx]: page
      }));
    };
    


    
    return (
      <div className="mt-3 space-y-3">
        <div className="p-3 bg-blue-50 rounded border">
          <div className="flex justify-between items-center mb-2">
            <div className="font-bold text-blue-700 flex items-center gap-2">
              <TableCellsIcon className="w-4 h-4" />
              Query Results ({data.length} rows returned)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => downloadCSV(data, `query_results_${Date.now()}.csv`)}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                title="Download complete results as CSV"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download CSV
              </button>
              <button
                onClick={() => loadChatMessages()}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                title="Refresh chat history"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-blue-100">
                  {keys.map(key => (
                    <th key={key} className="px-2 py-1 text-left font-medium text-blue-800 border-b">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayData.map((row, idx) => (
                  <tr key={startIndex + idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-25'}>
                    {keys.map(key => (
                      <td key={key} className="px-2 py-1 text-blue-900 border-b">
                        {String(row[key] || '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <SharedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={data.length}
                recordsPerPage={itemsPerPage}
                onPageChange={goToPage}
                showInfo={true}
                size="sm"
                color="blue"
              />
            )}
          </div>
        </div>
        
        <EnhancedChartVisualization data={data} messageIdx={messageIdx} />
      </div>
    );
  };

  // ✅ NEW: Function to toggle thinking process visibility
  const toggleThinking = (messageIdx) => {
    setCollapsedThinking(prev => ({
      ...prev,
      [messageIdx]: !prev[messageIdx]
    }));
  };

  const renderMessage = (msg, idx) => (
    <div key={idx} className={`mb-4 flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} items-end gap-2`}>
      {msg.type !== 'user' && (
        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mb-1">
          <SparklesIcon className="w-4 h-4 text-indigo-600" />
        </div>
      )}
      <div className={`rounded-2xl px-4 py-3 text-sm ${msg.type === 'user' ? 'user-message' : ''} ${
        msg.type === 'user'
          ? 'max-w-[75%] bg-indigo-600 text-white rounded-tr-none'
          : msg.isError
            ? 'w-full bg-red-50 border border-red-200 text-red-800 rounded-tl-none'
            : msg.isLoading
              ? 'w-full bg-white border border-gray-200 text-gray-700 shadow-sm rounded-tl-none'
              : 'w-full bg-white border border-gray-200 shadow-sm rounded-tl-none'
      }`}>
        <div className="space-y-3">
          {msg.isLoading ? (
            <div className="flex items-center gap-2">
              {[0,1,2].map(i => (
                <span key={i} className="w-2 h-2 rounded-full bg-indigo-400 inline-block animate-bounce" style={{animationDelay:`${i*0.15}s`}} />
              ))}
              <span className="text-sm text-gray-500 ml-1">Generating insight…</span>
            </div>
          ) : (
            <div>
              {msg.type === 'ai' ? (
                <ReactMarkdown 
                  components={{
                    p: ({children}) => <p className="text-sm whitespace-pre-wrap mb-2">{children}</p>,
                    strong: ({children}) => <strong className="font-bold text-gray-900">{children}</strong>,
                    em: ({children}) => <em className="italic">{children}</em>,
                    h1: ({children}) => <h1 className="text-lg font-bold mb-2 text-gray-800">{children}</h1>,
                    h2: ({children}) => <h2 className="text-base font-bold mb-2 text-gray-800">{children}</h2>,
                    h3: ({children}) => <h3 className="text-sm font-bold mb-1 text-gray-800">{children}</h3>,
                    ul: ({children}) => <ul className="list-disc list-inside ml-4 mb-2">{children}</ul>,
                    ol: ({children}) => <ol className="ml-4 mb-2" style={{listStyle: 'none'}}>{children}</ol>,
                    li: ({children}) => <li className="text-sm mb-1">{children}</li>,
                    code: ({children}) => <code className="bg-gray-200 px-1 rounded text-xs font-mono">{children}</code>
                  }}
                >
                  {/* For new messages with separate thinkingProcess, show only the content */}
                  {/* For old messages, if thinking is mixed in content, show only non-thinking parts */}
                  {msg.thinkingProcess 
                    ? 'Query executed successfully!' // When we have separate thinking process, show only success message
                    : (msg.content && msg.content.includes('**Analyze Question:**') 
                        ? msg.content.split('**Analyze Question:**')[0].trim() || 'Query executed successfully!'
                        : msg.content
                      )
                  }
                </ReactMarkdown>
              ) : (
                <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
              )}
            </div>
          )}
          
          {/* ✅ NEW: Collapsible Thinking Process Section (like GPT/Perplexity) */}
          {/* Handle both new messages with thinkingProcess property and old messages where thinking might be in content */}
          {(() => {
            const hasThinkingProcess = msg.thinkingProcess && msg.thinkingProcess.trim() && msg.type === 'ai';
            const hasOldThinkingInContent = msg.type === 'ai' && !msg.thinkingProcess && msg.content && msg.content.includes('**Analyze Question:**');
            const shouldShowReasoningSection = hasThinkingProcess || hasOldThinkingInContent;
            
            return shouldShowReasoningSection;
          })() && (
            <div className="border-t border-gray-200 pt-3">
              <button
                onClick={() => toggleThinking(idx)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200 w-full text-left"
              >
                {collapsedThinking[idx] ? (
                  <ChevronRightIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
                <span className="font-medium">Reasoning</span>
                <span className="text-xs text-gray-500 ml-auto">
                  {collapsedThinking[idx] ? 'Show reasoning' : 'Hide reasoning'}
                </span>
              </button>
              
              {/* ✅ Thinking process is expanded by default, hidden only when explicitly collapsed */}
              {!collapsedThinking[idx] && (
                <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200 text-xs text-gray-700">
                  <ReactMarkdown 
                    components={{
                      p: ({children}) => <p className="whitespace-pre-wrap font-mono leading-relaxed mb-2 text-xs">{children}</p>,
                      strong: ({children}) => <strong className="font-bold text-gray-900">{children}</strong>,
                      em: ({children}) => <em className="italic">{children}</em>,
                      h1: ({children}) => <h1 className="text-sm font-bold mb-2 text-gray-800">{children}</h1>,
                      h2: ({children}) => <h2 className="text-xs font-bold mb-2 text-gray-800">{children}</h2>,
                      h3: ({children}) => <h3 className="text-xs font-bold mb-1 text-gray-800">{children}</h3>,
                      ul: ({children}) => <ul className="list-disc list-inside ml-4 mb-2">{children}</ul>,
                      ol: ({children}) => <ol className="ml-4 mb-2" style={{listStyle: 'none'}}>{children}</ol>,
                      li: ({children}) => <li className="text-xs mb-1">{children}</li>,
                      code: ({children}) => <code className="bg-gray-200 px-1 rounded text-xs font-mono">{children}</code>
                    }}
                  >
                    {/* Use dedicated thinkingProcess or extract from content for backward compatibility */}
                    {msg.thinkingProcess || 
                     (msg.content && msg.content.includes('**Analyze Question:**') 
                       ? msg.content.split('**Analyze Question:**').slice(1).join('**Analyze Question:**') 
                       : ''
                     )}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )}
          
          {/* ✅ NEW: Show agent info if query was executed via agent */}
          {msg.agentInfo && (
            <div className="p-2 bg-indigo-50 rounded-lg border border-indigo-100">
              <div className="text-xs text-indigo-700 flex items-center gap-2">
                <CpuChipIcon className="w-4 h-4" />
                <strong>Executed via Agent:</strong> {msg.agentInfo.name}
                · <strong>Datasource:</strong> {msg.agentInfo.datasource} ({msg.agentInfo.datasource_type})
              </div>
            </div>
          )}
          
          {msg.insights && (
            <div className="p-3 bg-purple-50 rounded border border-purple-200">
              <div className="font-bold text-purple-700 mb-2 text-sm flex items-center gap-2">
                <ArrowTrendingUpIcon className="w-4 h-4" />
                AI Generated Insights:
              </div>
              <ReactMarkdown 
                components={{
                  p: ({children}) => <p className="text-sm text-purple-800 mb-2">{children}</p>,
                  strong: ({children}) => <strong className="font-bold text-purple-900">{children}</strong>,
                  em: ({children}) => <em className="italic text-purple-800">{children}</em>,
                  h1: ({children}) => <h1 className="text-lg font-bold mb-2 text-purple-900">{children}</h1>,
                  h2: ({children}) => <h2 className="text-base font-bold mb-2 text-purple-900">{children}</h2>,
                  h3: ({children}) => <h3 className="text-sm font-bold mb-1 text-purple-900">{children}</h3>,
                  ul: ({children}) => <ul className="list-disc list-inside ml-4 mb-2">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside ml-4 mb-2">{children}</ol>,
                  li: ({children}) => <li className="text-sm text-purple-800 mb-1">{children}</li>,
                  code: ({children}) => <code className="bg-purple-200 px-1 rounded text-xs font-mono">{children}</code>
                }}
              >
                {msg.insights}
              </ReactMarkdown>
            </div>
          )}
          
          {msg.sqlQuery && (
            <div className="p-3 bg-gray-100 rounded text-xs font-mono border">
              <div className="font-bold text-gray-700 mb-2 flex items-center gap-2">
                <WrenchScrewdriverIcon className="w-4 h-4" />
                Generated SQL Query:
              </div>
              <div className="text-gray-800 bg-white p-2 rounded border overflow-x-auto">
                {msg.sqlQuery}
              </div>
            </div>
          )}
          
          {msg.data && msg.data.length > 0 && renderDataTable(msg.data, idx)}
          
          <div className="flex justify-between items-center text-xs text-gray-600 pt-2 border-t">
            <div className="flex space-x-4">
              {msg.rowCount !== undefined && (
                <span className="flex items-center gap-1">
                  <TableCellsIcon className="w-3 h-3" />
                  {msg.rowCount} rows
                </span>
              )}
              {msg.executionTime && (
                <span className="flex items-center gap-1">
                  <ArrowPathIcon className="w-3 h-3" />
                  {msg.executionTime}s
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              {/* ✅ NEW: Feedback buttons for AI responses only */}
              {msg.type === 'ai' && !msg.isLoading && !msg.isError && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleThumbsUp(idx)}
                    disabled={messageFeedback[idx]?.submitted}
                    className={`p-1 rounded transition-colors ${
                      messageFeedback[idx]?.type === 'positive'
                        ? 'text-green-600 bg-green-50'
                        : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                    } ${messageFeedback[idx]?.submitted ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    title="Good response"
                  >
                    <HandThumbUpIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleThumbsDown(idx)}
                    disabled={messageFeedback[idx]?.submitted}
                    className={`p-1 rounded transition-colors ${
                      messageFeedback[idx]?.type === 'negative'
                        ? 'text-red-600 bg-red-50'
                        : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                    } ${messageFeedback[idx]?.submitted ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    title="Needs improvement"
                  >
                    <HandThumbDownIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              <div>{msg.timestamp}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ✅ NEW: Train Agent Section Component for Feedback Management
  const TrainAgentSection = ({ selectedAgent }) => {
    const [agentFeedback, setAgentFeedback] = useState([]);
    const [agentInstructions, setAgentInstructions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingFeedback, setEditingFeedback] = useState(null);
    const [editText, setEditText] = useState('');
    const [error, setError] = useState('');
    const [showAddInstruction, setShowAddInstruction] = useState(false);
    const [instructionText, setInstructionText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('feedback'); // 'feedback' or 'instruction'
    const [editingInstruction, setEditingInstruction] = useState(null);
    const [editInstructionText, setEditInstructionText] = useState('');

    const loadAgentFeedback = useCallback(async () => {
      if (!selectedAgent) return;
      
      try {
        setLoading(true);
        setError('');
        
        const response = await axios.get(
          `${API_CONFIG.API_BASE}/agents/${selectedAgent.agent_id}/feedback`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          setAgentFeedback(response.data.feedback || []);
        }
      } catch (error) {
        console.error('Error loading agent feedback:', error);
        setError('Failed to load feedback: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    }, [selectedAgent]);

    const loadAgentInstructions = useCallback(async () => {
      if (!selectedAgent) return;
      
      try {
        setLoading(true);
        setError('');
        
        // TODO: Replace with actual instructions endpoint once backend is ready
        const response = await axios.get(
          `${API_CONFIG.API_BASE}/agents/${selectedAgent.agent_id}/instructions`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          setAgentInstructions(response.data.instructions || []);
        }
      } catch (error) {
        console.error('Error loading agent instructions:', error);
        // Don't show error for instructions since endpoint might not exist yet
        setAgentInstructions([]);
      } finally {
        setLoading(false);
      }
    }, [selectedAgent]);

    // Load agent feedback and instructions when component mounts or agent changes
    useEffect(() => {
      if (selectedAgent) {
        if (selectedCategory === 'feedback') {
          loadAgentFeedback();
        } else if (selectedCategory === 'instruction') {
          loadAgentInstructions();
        }
      }
    }, [selectedAgent, selectedCategory, loadAgentFeedback, loadAgentInstructions]);

    const handleStartEdit = (feedback) => {
      setEditingFeedback(feedback.feedback_id);
      setEditText(feedback.feedback_text);
    };

    const handleCancelEdit = () => {
      setEditingFeedback(null);
      setEditText('');
    };

    const handleSaveEdit = async (feedbackId) => {
      try {
        setLoading(true);
        
        const response = await axios.put(
          `${API_CONFIG.API_BASE}/feedback/${feedbackId}`,
          null,
          {
            params: { feedback_text: editText },
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          // Update local state
          setAgentFeedback(prev => 
            prev.map(feedback => 
              feedback.feedback_id === feedbackId 
                ? { ...feedback, feedback_text: editText, updated_at: new Date().toISOString() }
                : feedback
            )
          );
          setEditingFeedback(null);
          setEditText('');
          console.log('✅ Feedback updated successfully');
        }
      } catch (error) {
        console.error('Error updating feedback:', error);
        setError('Failed to update feedback: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    const handleDelete = async (feedbackId) => {
      if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
        return;
      }
      
      try {
        setLoading(true);
        
        const response = await axios.delete(
          `${API_CONFIG.API_BASE}/feedback/${feedbackId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          // Remove from local state
          setAgentFeedback(prev => prev.filter(feedback => feedback.feedback_id !== feedbackId));
          console.log('✅ Feedback deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting feedback:', error);
        setError('Failed to delete feedback: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    const handleAddInstruction = async () => {
      if (!instructionText.trim()) {
        setError('Please enter an instruction');
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const response = await axios.post(
          `${API_CONFIG.API_BASE}/instructions`,
          {
            agent_id: selectedAgent.agent_id,
            instruction_text: instructionText.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          setInstructionText('');
          setShowAddInstruction(false);
          console.log('✅ Instruction added successfully');
          // Reload instructions to show the new one
          loadAgentInstructions();
        }
      } catch (error) {
        console.error('Error adding instruction:', error);
        setError('Failed to add instruction: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    const handleCancelInstruction = () => {
      setShowAddInstruction(false);
      setInstructionText('');
      setError('');
    };

    const handleStartEditInstruction = (instruction) => {
      setEditingInstruction(instruction.instruction_id);
      setEditInstructionText(instruction.instruction_text);
    };

    const handleCancelEditInstruction = () => {
      setEditingInstruction(null);
      setEditInstructionText('');
    };

    const handleSaveEditInstruction = async (instructionId) => {
      try {
        setLoading(true);
        
        const response = await axios.put(
          `${API_CONFIG.API_BASE}/instructions/${instructionId}`,
          null,
          {
            params: { instruction_text: editInstructionText },
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          // Update local state
          setAgentInstructions(prev => 
            prev.map(instruction => 
              instruction.instruction_id === instructionId 
                ? { ...instruction, instruction_text: editInstructionText, updated_at: new Date().toISOString() }
                : instruction
            )
          );
          setEditingInstruction(null);
          setEditInstructionText('');
          console.log('✅ Instruction updated successfully');
        }
      } catch (error) {
        console.error('Error updating instruction:', error);
        setError('Failed to update instruction: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    const handleToggleInstructionStatus = async (instructionId, currentStatus) => {
      try {
        setLoading(true);
        
        const response = await axios.patch(
          `${API_CONFIG.API_BASE}/instructions/${instructionId}/toggle`,
          null,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          // Update local state
          setAgentInstructions(prev => 
            prev.map(instruction => 
              instruction.instruction_id === instructionId 
                ? { ...instruction, is_active: response.data.is_active, updated_at: new Date().toISOString() }
                : instruction
            )
          );
          console.log(`✅ Instruction ${response.data.is_active ? 'enabled' : 'disabled'} successfully`);
        }
      } catch (error) {
        console.error('Error toggling instruction status:', error);
        setError('Failed to toggle instruction status: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    const handleDeleteInstruction = async (instructionId) => {
      if (!window.confirm('Are you sure you want to delete this instruction? This action cannot be undone.')) {
        return;
      }
      
      try {
        setLoading(true);
        
        const response = await axios.delete(
          `${API_CONFIG.API_BASE}/instructions/${instructionId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.success) {
          // Remove from local state
          setAgentInstructions(prev => prev.filter(instruction => instruction.instruction_id !== instructionId));
          console.log('✅ Instruction deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting instruction:', error);
        setError('Failed to delete instruction: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    if (!selectedAgent) {
      return (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🎯</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Train Agent</h3>
          <p className="text-gray-600 mb-4">Select an agent to view and manage feedback</p>
          <button 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            onClick={() => setAskDBActiveTab('agents')}
          >
            Select Agent
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Train Agent: {selectedAgent.agent_name}</h2>
            <p className="text-gray-600 mt-1">Manage feedback and instructions to improve AI responses</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Category Dropdown */}
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="feedback">Feedback</option>
                <option value="instruction">Instructions</option>
              </select>
            </div>
            
            <div className="text-sm text-gray-500">
              Agent ID: {selectedAgent.agent_id.substring(0, 8)}... • {
                selectedCategory === 'feedback' 
                  ? `${agentFeedback.length} feedback entries`
                  : `${agentInstructions.length} instructions`
              }
            </div>
            
            {/* Conditional Add Instruction Button */}
            {selectedCategory === 'instruction' && (
              <button
                onClick={() => setShowAddInstruction(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Add Instruction
              </button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Add Instruction Form */}
        {showAddInstruction && (
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Generic Instruction</h3>
              <button
                onClick={handleCancelInstruction}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instruction Text
                </label>
                <textarea
                  value={instructionText}
                  onChange={(e) => setInstructionText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows={4}
                  placeholder="Enter generic instruction for the AI agent (e.g., 'Always format numbers with commas', 'Use professional tone in responses', etc.)"
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleAddInstruction}
                  disabled={!instructionText.trim() || loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                >
                  {loading ? 'Adding...' : 'Add Instruction'}
                </button>
                <button
                  onClick={handleCancelInstruction}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !editingFeedback && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              Loading {selectedCategory === 'feedback' ? 'feedback' : 'instructions'}...
            </p>
          </div>
        )}

        {/* Data Table */}
        {!loading && (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {selectedCategory === 'feedback' ? (
              // Feedback Table
              agentFeedback.length === 0 ? (
                <div className="text-center py-12">
                  <div className="flex justify-center mb-4">
                    <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Feedback Yet</h3>
                  <p className="text-gray-600">
                    Start using this agent in the Ask DB section to collect feedback and train responses.
                  </p>
                  <button 
                    className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    onClick={() => setAskDBActiveTab('askdb')}
                  >
                    Go to Ask DB
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Question
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Feedback Content
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Feedback Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {agentFeedback.map((feedback) => (
                        <tr key={feedback.feedback_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs break-words">
                              {feedback.user_question}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {editingFeedback === feedback.feedback_id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  rows={3}
                                  placeholder="Enter your feedback..."
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSaveEdit(feedback.feedback_id)}
                                    disabled={!editText.trim() || loading}
                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-900 max-w-md break-words">
                                {feedback.feedback_text}
                                {feedback.updated_at && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Updated: {new Date(feedback.updated_at).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              feedback.feedback_type === 'positive' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {feedback.feedback_type === 'positive' ? 'Positive' : 'Negative'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(feedback.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              {editingFeedback !== feedback.feedback_id && (
                                <>
                                  <button
                                    onClick={() => handleStartEdit(feedback)}
                                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 flex items-center gap-1"
                                    title="Edit feedback"
                                  >
                                    <PencilIcon className="w-3 h-3" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(feedback.feedback_id)}
                                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-1"
                                    title="Delete feedback"
                                  >
                                    <TrashIcon className="w-3 h-3" />
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              // Instructions Table
              agentInstructions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="flex justify-center mb-4">
                    <ClipboardDocumentListIcon className="w-16 h-16 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Instructions Yet</h3>
                  <p className="text-gray-600">
                    Add generic instructions to guide the AI agent's behavior and responses.
                  </p>
                  <button 
                    className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    onClick={() => setShowAddInstruction(true)}
                  >
                    Add First Instruction
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Instruction Text
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {agentInstructions.map((instruction) => (
                        <tr key={instruction.instruction_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            {editingInstruction === instruction.instruction_id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editInstructionText}
                                  onChange={(e) => setEditInstructionText(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  rows={3}
                                  placeholder="Enter instruction text..."
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSaveEditInstruction(instruction.instruction_id)}
                                    disabled={!editInstructionText.trim() || loading}
                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEditInstruction}
                                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-900 max-w-md break-words">
                                {instruction.instruction_text}
                                {instruction.updated_at && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Updated: {new Date(instruction.updated_at).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleToggleInstructionStatus(instruction.instruction_id, instruction.is_active)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 items-center gap-1 ${
                                instruction.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                              title={`Click to ${instruction.is_active ? 'disable' : 'enable'}`}
                            >
                              {instruction.is_active ? (
                                <>
                                  <CheckCircleIcon className="w-3 h-3" />
                                  Active
                                </>
                              ) : (
                                <>
                                  <PauseIcon className="w-3 h-3" />
                                  Disabled
                                </>
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(instruction.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              {editingInstruction !== instruction.instruction_id && (
                                <>
                                  <button
                                    onClick={() => handleStartEditInstruction(instruction)}
                                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 flex items-center gap-1"
                                    title="Edit instruction"
                                  >
                                    <PencilIcon className="w-3 h-3" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteInstruction(instruction.instruction_id)}
                                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-1"
                                    title="Delete instruction"
                                  >
                                    <TrashIcon className="w-3 h-3" />
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const renderHomeContent = () => (
    <div className="space-y-3">

      {/* ── Hero Banner (gradient — keep as-is) ────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl">
        <div className="bg-gradient-to-br from-indigo-700 via-violet-800 to-purple-900 px-8 py-12">
          {/* Decorative glowing orbs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-violet-500 rounded-full opacity-20 blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
          <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-indigo-400 rounded-full opacity-15 blur-3xl pointer-events-none"></div>
          <div className="absolute top-1/2 right-1/3 w-40 h-40 bg-purple-300 rounded-full opacity-10 blur-2xl pointer-events-none"></div>
          <div className="relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs text-blue-100 mb-3">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                AI Engine Active
              </div>
              <h2 className="text-3xl font-extrabold text-white mb-2 tracking-tight">
                {getGreeting()}, {user?.first_name || user?.username || 'User'} !
              </h2>
              <p className="text-blue-200 text-sm max-w-lg">
                Automate workflows, query your data, and let AI agents handle the heavy lifting — all in one place.
              </p>

              {/* ── Stat strip ──────────────────────────────────── */}
              <div className="mt-6 pt-5 border-t border-white/20 grid grid-cols-5 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                    <ShareIcon className="w-5 h-5 text-indigo-200" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white leading-none">
                      {homeStats ? homeStats.workflows : <span className="inline-block w-8 h-5 bg-white/20 rounded animate-pulse" />}
                    </p>
                    <p className="text-xs text-blue-200 mt-0.5">Workflows</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                    <SparklesIcon className="w-5 h-5 text-purple-200" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white leading-none">
                      {homeStats ? homeStats.cognix_agents : <span className="inline-block w-8 h-5 bg-white/20 rounded animate-pulse" />}
                    </p>
                    <p className="text-xs text-blue-200 mt-0.5">Cognix Agents</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                    <CpuChipIcon className="w-5 h-5 text-teal-200" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white leading-none">
                      {homeStats ? homeStats.db_agents : <span className="inline-block w-8 h-5 bg-white/20 rounded animate-pulse" />}
                    </p>
                    <p className="text-xs text-blue-200 mt-0.5">DB Agents</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                    <TableCellsIcon className="w-5 h-5 text-orange-200" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white leading-none">
                      {homeStats ? homeStats.excel_agents : <span className="inline-block w-8 h-5 bg-white/20 rounded animate-pulse" />}
                    </p>
                    <p className="text-xs text-blue-200 mt-0.5">Excel Agents</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                    <CurrencyDollarIcon className="w-5 h-5 text-amber-200" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white leading-none">
                      {homeStats ? homeStats.total_tokens : <span className="inline-block w-8 h-5 bg-white/20 rounded animate-pulse" />}
                    </p>
                    <p className="text-xs text-blue-200 mt-0.5">Tokens Used (Last 30 Days)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Charts row ──────────────────────────────────────────── */}
      <HomeStatsRow />

      {/* ── Row 2: AI Suggestions + Recent Activity ─────────────────── */}
      <div className="grid grid-cols-5 gap-4">

        {/* Left: AI Suggestions */}
        <div className="col-span-3 bg-gradient-to-br from-blue-100 via-indigo-100/80 to-blue-200/60 rounded-2xl border border-blue-300 shadow-sm p-5">
          <div className="mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-yellow-500">💡</span> AI Suggestions For You
            </h3>
          </div>
          <div className="space-y-2.5">

            {/* Natural Language SQL */}
            <div onClick={() => { setIsAgentMenuOpen(true); setActiveTab('database-agent'); }} className="flex items-center gap-3 p-3 bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors group cursor-pointer">
              <div className="w-9 h-9 bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Natural Language SQL</p>
                <p className="text-xs text-gray-500">Ask your database questions in plain English — AI writes the SQL</p>
              </div>
            </div>

            {/* Smart AI Agents */}
            <div onClick={() => { setIsAgentMenuOpen(true); setActiveTab('cognix-agent'); }} className="flex items-center gap-3 p-3 bg-indigo-50/80 backdrop-blur-sm rounded-xl border border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 transition-colors group cursor-pointer">
              <div className="w-9 h-9 bg-indigo-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Smart AI Agents</p>
                <p className="text-xs text-gray-500">Build custom AI agents with tools connected to your data</p>
              </div>
            </div>

            {/* Workflow Automation */}
            <div onClick={() => setActiveTab('automate-task')} className="flex items-center gap-3 p-3 bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors group cursor-pointer">
              <div className="w-9 h-9 bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Workflow Automation</p>
                <p className="text-xs text-gray-500">Schedule recurring queries and automate reports with zero code</p>
              </div>
            </div>

          </div>
        </div>

        {/* Right: Recent Activity */}
        <div className="col-span-2 bg-gradient-to-br from-violet-100 via-purple-100 to-indigo-100/80 rounded-2xl border border-purple-300 shadow-sm p-5">
          <div className="mb-3">
            <h3 className="font-semibold text-gray-800">Recent Activity</h3>
          </div>
          <RecentActivityFeed limit={5} onAgentClick={handleAgentClickFromActivity} compact={true} />
        </div>

      </div>

      {/* ── Row 3: Quick Access (full width) ────────────────────────── */}
      <div className="bg-gradient-to-br from-indigo-100 via-blue-100/80 to-purple-100/60 rounded-2xl border border-indigo-300 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <h3 className="font-semibold text-gray-800">Quick Access</h3>
        </div>
        <div className="grid grid-cols-4 gap-3">

          {/* Upload Excel/CSV */}
          <button
            onClick={() => setActiveTab('excel-upload')}
            className="flex items-start gap-3 p-4 bg-emerald-50/80 backdrop-blur-sm rounded-xl border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">Upload Excel/CSV</p>
              <p className="text-xs text-gray-500 mt-0.5">Import your spreadsheet data</p>
            </div>
          </button>

          {/* Analytics */}
          <button
            onClick={() => setActiveTab('analytics')}
            className="flex items-start gap-3 p-4 bg-orange-50/80 backdrop-blur-sm rounded-xl border border-orange-200 hover:border-orange-400 hover:bg-orange-100 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-orange-700 transition-colors">Analytics</p>
              <p className="text-xs text-gray-500 mt-0.5">View charts and data insights</p>
            </div>
          </button>

          {/* Settings */}
          <button
            onClick={() => setActiveTab('settings')}
            className="flex items-start gap-3 p-4 bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200 hover:border-blue-400 hover:bg-blue-100 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">Settings</p>
              <p className="text-xs text-gray-500 mt-0.5">Manage your preferences</p>
            </div>
          </button>

          {/* Data Preview */}
          <button
            onClick={() => setActiveTab('data-preview')}
            className="flex items-start gap-3 p-4 bg-purple-50/80 backdrop-blur-sm rounded-xl border border-purple-200 hover:border-purple-400 hover:bg-purple-100 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">Data Preview</p>
              <p className="text-xs text-gray-500 mt-0.5">View your data tables</p>
            </div>
          </button>

        </div>
      </div>

    </div>
  );

  // ✅ UPDATED: Database Agent content with mode-based rendering
  const renderDatabaseAgentContent = () => {
    // If agent is selected, show AskDB and Train Agent interface
    if (showAgentInteractionMode && selectedAgent) {
      return (
        <div className="flex flex-col h-full">
          {/* Compact Tabs with Back Button */}
          <div className="border-b border-gray-200 flex-shrink-0">
            <nav className="-mb-px flex justify-between items-center">
              <div className="flex space-x-8">
                <button
                  onClick={() => setAskDBActiveTab('askdb')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    askDBActiveTab === 'askdb'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  AskDB
                </button>
                <button
                  onClick={() => setAskDBActiveTab('train-agent')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    askDBActiveTab === 'train-agent'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Train Agent
                </button>
              </div>
              <button
                onClick={handleBackToAgents}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                <span>←</span>
                Back to Agents
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {<Suspense fallback={<div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading…</div>}>{renderAgentInteractionTabContent()}</Suspense>}
          </div>
        </div>
      );
    }

    // Default: Show agent selection interface (no tabs)
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Choose or Create an Agent</h2>
            <p className="text-gray-600 mt-1">Select an existing agent or create a new one to start querying your database</p>
          </div>
          
          {!showCreateAgentForm && (
            <button 
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              onClick={() => setShowCreateAgentForm(true)}
            >
              <PlusIcon className="w-4 h-4" />
              Create New Agent
            </button>
          )}
        </div>

        {showCreateAgentForm ? (
          <div className="bg-white rounded-lg border shadow-sm">
            <CreateAgentForm 
              onAgentCreated={handleAgentCreated}
              onCancel={() => setShowCreateAgentForm(false)}
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <React.Suspense fallback={
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading agents...</span>
              </div>
            }>
              <AgentList 
                onAgentClick={handleAgentSelected}
                onEditAgent={handleAgentSelected}
                refreshTrigger={agentRefreshTrigger}
                showAsLinks={true}
              />
            </React.Suspense>
          </div>
        )}
      </div>
    );
  };

  // Cognix Agent Navigation Handlers
  const handleCreateCognixAgent = () => {
    setSelectedCognixAgent(null);
    setCognixAgentError('');
    setCognixAgentLoading(false);
    setCognixAgentView('builder');
  };

  const handleEditCognixAgent = async (agent) => {
    if (!agent?.agent_id) return;
    try {
      setCognixAgentLoading(true);
      setCognixAgentError('');
      const detailedAgent = await cognixAgentService.getAgent(agent.agent_id);
      setSelectedCognixAgent(detailedAgent || agent);
      setCognixAgentView('builder');
    } catch (error) {
      console.error('❌ Error loading Cognix agent details:', error);
      setCognixAgentError(error.message || 'Failed to load agent details. Please try again.');
    } finally {
      setCognixAgentLoading(false);
    }
  };

  const handleChatWithCognixAgent = (agent) => {
    setSelectedCognixAgent(agent);
    setCognixAgentView('chat');
  };

  const handleBackToCognixList = () => {
    setSelectedCognixAgent(null);
    setCognixAgentView('list');
    setCognixAgentRefreshTrigger(prev => prev + 1);
    setCognixAgentError('');
    setCognixAgentLoading(false);
  };

  const handleCognixAgentSaved = () => {
    handleBackToCognixList();
  };

  // Render Cognix Agent Content
  const renderCognixAgentContent = () => {
    // Chat and builder views bypass the tab layout
    if (cognixAgentView === 'chat' && selectedCognixAgent) {
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Chat with {selectedCognixAgent.agent_name}</h2>
              <p className="text-gray-600 mt-1">{selectedCognixAgent.description}</p>
            </div>
            <button
              onClick={handleBackToCognixList}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <span>←</span>
              Back to Agents
            </button>
          </div>
          <div className="bg-white rounded-lg border shadow-sm">
            <Suspense fallback={<div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading…</div>}>
              <CognixAgentChat agent={selectedCognixAgent} onBack={handleBackToCognixList} />
            </Suspense>
          </div>
        </div>
      );
    }

    if (cognixAgentView === 'builder') {
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedCognixAgent ? 'Edit Cognix Agent' : 'Create Cognix Agent'}
            </h2>
            <button
              onClick={handleBackToCognixList}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <span>←</span>
              Back to Agents
            </button>
          </div>
          {cognixAgentError && (
            <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {cognixAgentError}
            </div>
          )}
          <div className="bg-white rounded-lg border shadow-sm">
            {cognixAgentLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-600">
                <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                Loading agent configuration...
              </div>
            ) : (
              <CognixAgentBuilder
                agent={selectedCognixAgent}
                onSave={handleCognixAgentSaved}
                onCancel={handleBackToCognixList}
              />
            )}
          </div>
        </div>
      );
    }

    // Default: tabbed list view (Agent | Tools)
    return (
      <div className="space-y-6">
        {/* Tab header */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setCognixPageTab('agent')}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                cognixPageTab === 'agent'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CpuChipIcon className="h-4 w-4 mr-2" />
              Agents
            </button>
            <button
              onClick={() => setCognixPageTab('tools')}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                cognixPageTab === 'tools'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BoltIcon className="h-4 w-4 mr-2" />
              Tools
            </button>
          </div>
        </div>

        {/* Tab content */}
        {cognixPageTab === 'agent' && (
          <>
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Cognix AI Agents</h2>
                <p className="text-gray-600 mt-1">Create and manage AI agents with custom tools and connections</p>
              </div>
              <button
                onClick={handleCreateCognixAgent}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Create New Agent
              </button>
            </div>
            <div className="bg-white rounded-lg border shadow-sm p-6">
              <CognixAgentList
                onChatClick={handleChatWithCognixAgent}
                onEditClick={handleEditCognixAgent}
                refreshTrigger={cognixAgentRefreshTrigger}
              />
            </div>
          </>
        )}

        {cognixPageTab === 'tools' && (
          <div className="space-y-6">
            <CognixToolList refreshTrigger={cognixToolRefreshTrigger} />
          </div>
        )}
      </div>
    );
  };

  // ✅ NEW: Render Cognix Tools content
  // eslint-disable-next-line no-unused-vars
  const renderCognixToolsContent = () => (
    <div className="space-y-6">
      <CognixToolList refreshTrigger={cognixToolRefreshTrigger} />
    </div>
  );

  // ✅ NEW: Render content for agent interaction (AskDB/Train Agent)
  const renderAgentInteractionTabContent = () => {
    switch (askDBActiveTab) {
      case 'askdb':
        return (
          <div className="flex flex-col h-full">
            <div className="bg-white rounded-lg border shadow-sm flex flex-col flex-1 min-h-0">
              <div className="p-4 flex flex-col flex-1 min-h-0">
                <div className="flex flex-col flex-1 min-h-0 gap-4">

                  {/* ✅ FIXED: Safe handling of selected_tables */}
                  {selectedAgent && (
                    <div className="flex-shrink-0 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CpuChipIcon className="w-5 h-5 text-indigo-500" />
                          <div>
                            <div className="font-medium text-gray-800 text-sm">Using Agent: {selectedAgent.agent_name}</div>
                            <div className="text-xs text-gray-500">
                              Datasource ID: {selectedAgent.datasource_id} · Tables: {(selectedAgent.selected_tables || []).join(', ') || 'No tables selected'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {messages.length > 0 && (
                            <button
                              onClick={clearAllConversations}
                              disabled={isLoading}
                              title="Clear chat"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedAgent(null);
                              setShowAgentInteractionMode(false);
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
                          >
                            Change Agent
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!selectedAgent && (
                    <div className="flex-shrink-0 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                        <div>
                          <div className="font-medium text-yellow-900">No Agent Selected</div>
                          <div className="text-sm text-yellow-700">
                            Please select an agent first to query your database with AI assistance.
                          </div>
                        </div>
                        <button 
                          className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                          onClick={handleBackToAgents}
                        >
                          Select Agent
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div
                    ref={messagesContainerRef}
                    className="p-5 flex-1 min-h-0 overflow-y-auto bg-white w-full max-w-full chat-messages scrollable-container"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    {messages.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="flex justify-center mb-4">
                          <SparklesIcon className="w-16 h-16 text-indigo-400" />
                        </div>
                        <p className="text-gray-500 text-lg mb-2">
                          Welcome to CognixAI Database Chat!
                        </p>
                        <p className="text-gray-400 text-sm">
                          Ask me anything about your database in natural language.<br/>
                          I'll analyze your query, generate SQL, and provide intelligent insights.<br/>
                          <strong>Features:</strong> Dynamic Processing • Smart Insights • Interactive Charts<br/>
                          <strong>Example:</strong> "Show me user growth trends" or "Analyze sales performance"
                        </p>
                      </div>
                    ) : (
                      messages.map((msg, idx) => renderMessage(msg, idx))
                    )}
                  </div>

                  <form onSubmit={handleQuerySubmit} className="flex-shrink-0">
                    <div className="flex items-end gap-3 bg-gray-50 border border-gray-300 rounded-xl p-3 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-indigo-400 transition-all">
                      <textarea
                        rows={1}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (!isLoading && query.trim() && selectedAgent) handleQuerySubmit(e);
                          }
                        }}
                        placeholder={selectedAgent
                          ? `Ask ${selectedAgent.agent_name} about your data… (Enter to send)`
                          : "Select an agent first to start querying…"
                        }
                        className="flex-1 bg-transparent text-sm text-gray-800 resize-none outline-none max-h-32"
                        disabled={isLoading || !selectedAgent}
                        autoComplete="off"
                        spellCheck="false"
                        style={{ minHeight: '1.5rem' }}
                      />
                      <button
                        type="submit"
                        disabled={isLoading || !query.trim() || !selectedAgent}
                        className={`p-2 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                          isLoading || !query.trim() || !selectedAgent
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                      >
                        {isLoading
                          ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                          : <MagnifyingGlassIcon className="w-4 h-4" />
                        }
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 px-1">Enter to send · Shift+Enter for new line</p>
                    
                    <div className="text-xs text-gray-500 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <InformationCircleIcon className="w-4 h-4" />
                        <strong>Enhanced AI Analysis:</strong> {selectedAgent ? 'Agent-Powered' : 'Select Agent'} + Dynamic Processing + Smart Insights + Interactive Visualizations
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        );

      case 'train-agent':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Train Agent</h2>
            </div>
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-6">
                <TrainAgentSection selectedAgent={selectedAgent} />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <div className="text-center">
              <h3 className="text-lg font-medium mb-2">Select an Option</h3>
              <p>Choose AskDB to query your database or Train Agent to improve responses.</p>
            </div>
          </div>
        );
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return renderHomeContent();
      
      case 'database-agent':
        return renderDatabaseAgentContent();

      case 'cognix-agent':
        return renderCognixAgentContent();

      case 'analytics':
        return <AnalyticsDashboard />;
      
      case 'data-preview':
        return <DataPreview />;
      
      // case 'business-glossary': return <BusinessGlossary />; // hidden: replaced by Knowledge Base
      case 'automate-task':
        return <AutomateTask />;
      
      case 'excel-upload':
        return <ExcelUploadPage />;

      case 'excel-agent':
        return <ExcelAgent />;

      case 'data-sources':
        return (
          <DataSourcesPage
            quickStartConfig={dataSourcesQuickStart}
            onQuickStartHandled={() => setDataSourcesQuickStart(null)}
          />
        );

      case 'knowledge-base':
        return (
          <div className="p-6 h-full overflow-y-auto">
            <KnowledgeBase />
          </div>
        );

      case 'workflow':
        return renderWorkflowContent();

      case 'ai-assistant':
        return (
          <div className="p-6 h-full overflow-hidden">
            <AIAssistant />
          </div>
        );

      case 'settings':
        return <Settings />;

      default:
        return renderHomeContent();
    }
  };

  const renderWorkflowContent = () => {
    if (workflowView === 'builder') {
      return (
        <div className="h-full flex flex-col">
          <WorkflowBuilder
            workflow={editingWorkflow}
            projectId={editingWorkflow?.project_id ?? workflowProjectId}
            onBack={() => { setWorkflowView('projects'); setEditingWorkflow(null); }}
            onSaved={() => {
              setWorkflowView('projects');
              setEditingWorkflow(null);
              setWorkflowRefreshTrigger(t => t + 1);
              // workflowProjectId intentionally kept so WorkflowProjects restores the active project
            }}
          />
        </div>
      );
    }

    return (
      <div className="h-full overflow-hidden">
        <WorkflowProjects
          initialProjectId={workflowProjectId}
          onEditWorkflow={(wf, projectId) => {
            setEditingWorkflow(wf);
            setWorkflowProjectId(projectId || null);
            setWorkflowView('builder');
          }}
          onRunWorkflow={(wf) => setRunningWorkflow(wf)}
        />
        {runningWorkflow && (
          <WorkflowRunModal
            workflow={runningWorkflow}
            onClose={() => setRunningWorkflow(null)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-hidden">
      {/* Vertical Sidebar - Fixed */}
      <div className="w-64 flex flex-col h-screen fixed" style={{ background: '#ffffff', borderRight: '1px solid rgba(15,23,42,0.08)' }}>
        {/* Logo/Header */}
        <div className="px-4 flex-shrink-0" style={{ paddingTop: '3.5rem', paddingBottom: '1rem' }}>
          <img
            src="/cognixaihub_new_logo.png"
            alt="CognixAI Hub"
            style={{ width: 176, height: 58, objectFit: 'contain' }}
          />
        </div>

        {/* Navigation - Vertically centered */}
        <nav className="flex-1 px-3 overflow-y-hidden flex flex-col justify-center" style={{ paddingTop: '0', marginTop: '-4rem' }}>
          <ul className="space-y-0.5">
            {sidebarTabs.map((tab) => {
              const IconComponent = tab.icon;
              const isChildActive = tab.children?.some(child => child.id === activeTab);
              const isActive = activeTab === tab.id || isChildActive;
              const isAgentParent = tab.id === 'agent';

              return (
                <li key={tab.id}>
                  <button
                    ref={isAgentParent ? agentButtonRef : undefined}
                    onClick={() => {
                      if (isAgentParent) {
                        const willOpen = !isAgentMenuOpen;
                        if (willOpen) {
                          const rect = agentButtonRef.current?.getBoundingClientRect();
                          if (rect) setAgentFlyoutPos({ top: rect.top, left: rect.right + 8 });
                          if (!isChildActive && tab.children?.length) setActiveTab(tab.children[0].id);
                        }
                        setIsAgentMenuOpen(willOpen);
                      } else {
                        setActiveTab(tab.id);
                      }
                    }}
                    className={`w-full flex items-center px-3 py-2 text-left rounded-r-lg transition-colors ${
                      isActive
                        ? 'text-slate-900 border-l-[3px] border-purple-500'
                        : 'text-slate-500 hover:text-slate-900 border-l-[3px] border-transparent'
                    }`}
                    style={isActive ? { background: 'rgba(124,58,237,0.08)' } : undefined}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = ''; }}
                  >
                    <IconComponent className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span className="font-medium truncate">{tab.name}</span>
                    {tab.children && (
                      <ChevronRightIcon
                        className={`w-4 h-4 ml-auto text-slate-400 transition-transform ${
                          isAgentMenuOpen ? 'rotate-90' : ''
                        }`}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Agent flyout — rendered via portal so it floats above everything */}
        {isAgentMenuOpen && ReactDOM.createPortal(
          <>
            {/* Click-outside overlay */}
            <div className="fixed inset-0 z-[999]" onClick={() => setIsAgentMenuOpen(false)} />
            <div
              className="fixed z-[1000] rounded-xl py-2 min-w-[190px]"
              style={{
                top: agentFlyoutPos.top,
                left: agentFlyoutPos.left,
                background: '#1a1730',
                border: '1px solid rgba(139,92,246,0.35)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              }}
            >
              <div className="px-3 pb-1.5 mb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Agents</span>
              </div>
              {sidebarTabs.find(t => t.id === 'agent')?.children?.map((child) => (
                <button
                  key={child.id}
                  onClick={() => { setActiveTab(child.id); setIsAgentMenuOpen(false); }}
                  className={`w-full flex items-center px-3 py-2 text-left text-sm font-medium transition-colors ${
                    activeTab === child.id ? 'text-white' : 'text-slate-400 hover:text-white'
                  }`}
                  style={activeTab === child.id ? { background: 'rgba(139,92,246,0.2)' } : undefined}
                  onMouseEnter={e => { if (activeTab !== child.id) e.currentTarget.style.background = 'rgba(139,92,246,0.08)'; }}
                  onMouseLeave={e => { if (activeTab !== child.id) e.currentTarget.style.background = ''; }}
                >
                  <span className={`w-1.5 h-1.5 rounded-full mr-2.5 flex-shrink-0 ${activeTab === child.id ? 'bg-purple-400' : 'bg-transparent'}`} />
                  {child.name}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}

        {/* Notification modal — centered popup */}
        {showNotifications && ReactDOM.createPortal(
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-[998] bg-black/50 backdrop-blur-sm" onClick={() => setShowNotifications(false)} />
            {/* Modal */}
            <div
              className="fixed z-[999] rounded-2xl shadow-2xl overflow-hidden"
              data-notification-panel
              style={{
                background: 'linear-gradient(145deg, #1a1730, #1e1b4b)',
                border: '1px solid rgba(139,92,246,0.25)',
                width: '420px',
                maxHeight: '80vh',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2.5">
                  <BellIcon className="w-5 h-5 text-indigo-400" />
                  <span className="text-base font-semibold text-white">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="text-xs bg-indigo-500/30 text-indigo-300 px-2 py-0.5 rounded-full font-medium">{unreadCount} new</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {unreadCount > 0 && (
                    <button
                      onClick={async () => { await notificationService.markAllRead(); fetchNotifications(); }}
                      className="text-xs text-indigo-300 hover:text-white transition-colors"
                    >Mark all read</button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      onClick={async () => { await notificationService.deleteAll(); fetchNotifications(); }}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors"
                    >Clear all</button>
                  )}
                  <button onClick={() => setShowNotifications(false)} className="text-white/40 hover:text-white transition-colors ml-1">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              {/* Body */}
              <div className="overflow-y-auto divide-y" style={{ maxHeight: 'calc(80vh - 64px)', borderColor: 'rgba(255,255,255,0.05)' }}>
                {notifications.length === 0 ? (
                  <div className="px-5 py-16 text-center">
                    <BellIcon className="w-10 h-10 text-white/10 mx-auto mb-3" />
                    <p className="text-sm text-white/40">No notifications yet</p>
                  </div>
                ) : notifications.slice(0, 20).map(n => (
                  <div
                    key={n.id}
                    onClick={async () => { if (!n.is_read) { await notificationService.markRead(n.id); fetchNotifications(); } }}
                    className={`px-5 py-4 flex gap-3 cursor-pointer transition-colors ${!n.is_read ? 'hover:bg-white/5' : 'opacity-60 hover:bg-white/5'}`}
                    style={!n.is_read ? { background: 'rgba(99,102,241,0.06)' } : {}}
                  >
                    <span className="mt-0.5 flex-shrink-0 text-lg">{n.status === 'success' ? '✅' : '❌'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/90 leading-snug break-words">{n.message}</p>
                      <p className="text-xs text-white/35 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}

        {/* User section - Always visible at bottom */}
        <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(15,23,42,0.08)' }}>
          <div className="relative profile-dropdown">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="w-full flex items-center space-x-3 p-2 rounded-lg transition-colors"
              style={{ background: 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                {(user?.first_name || user?.username || 'U')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {user?.first_name || user?.username || 'User'}
                </div>
              </div>
              <ChevronDownIcon
                className={`w-4 h-4 text-slate-400 transition-transform ${
                  showProfileDropdown ? 'transform rotate-180' : ''
                }`}
              />
            </button>

            {/* Profile Dropdown Menu */}
            {showProfileDropdown && (<>
              <div className="absolute bottom-full left-0 right-0 mb-1 rounded-lg shadow-xl py-1 z-50" style={{ background: '#ffffff', border: '1px solid rgba(15,23,42,0.08)' }}>
                <button
                  ref={notifButtonRef}
                  onClick={() => {
                    const rect = notifButtonRef.current?.getBoundingClientRect();
                    if (rect) setNotifPanelPos({ bottom: window.innerHeight - rect.top + 8, left: rect.right + 8 });
                    setShowNotifications(s => !s);
                    setShowProfileDropdown(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-slate-600 transition-colors"
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div className="relative mr-3">
                    <BellIcon className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[12px] h-3 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center px-0.5">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  Notifications
                  {unreadCount > 0 && (
                    <span className="ml-auto text-xs bg-red-500/10 text-red-500 px-1.5 py-0.5 rounded-full font-medium">{unreadCount}</span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setActiveTab('settings');
                    setShowProfileDropdown(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-slate-600 transition-colors"
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <CogIcon className="w-4 h-4 mr-3" />
                  Settings
                </button>
                <hr style={{ borderColor: 'rgba(15,23,42,0.08)' }} />
                <button
                  onClick={() => {
                    logout();
                    setShowProfileDropdown(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-red-500 transition-colors"
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3" />
                  Logout
                </button>
              </div>

            </>)}

          </div>
        </div>
      </div>

      {/* Main Content - Offset by sidebar width */}
      <div className="flex-1 flex flex-col ml-64 min-w-0 overflow-hidden">
        {/* Content Area */}
        <main className="flex-1 p-4 overflow-hidden">
          <div className="h-full w-full max-w-full overflow-auto">
            {<Suspense fallback={<div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading…</div>}>{renderTabContent()}</Suspense>}
          </div>
        </main>
      </div>

      {/* ✅ NEW: Feedback Modal */}
      {feedbackModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="w-5 h-5" />
                Provide Feedback
              </h3>
              <button
                onClick={closeFeedbackModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* User Question (Non-editable) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Question:
                </label>
                <div className="w-full p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                  {feedbackModal.userQuestion}
                </div>
              </div>

              {/* Feedback Text Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What could be improved? *
                </label>
                <textarea
                  value={feedbackModal.feedbackText}
                  onChange={(e) => setFeedbackModal(prev => ({
                    ...prev,
                    feedbackText: e.target.value
                  }))}
                  placeholder="Please share what was wrong with the response or how it could be improved..."
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeFeedbackModal}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFeedbackSubmit}
                  disabled={!feedbackModal.feedbackText.trim()}
                  className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    feedbackModal.feedbackText.trim()
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Submit Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
